import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import MainLayout from "../components/layout/MainLayout";
import ClubList from "../features/clubs/ClubList";
import ClubFormModal from "../features/clubs/ClubFormModal";
import ClubDetailModal from "../features/clubs/ClubDetailModal";
import ClubMembersModal from "../features/clubs/ClubMembersModal";
import ClubEventsModal from "../features/clubs/ClubEventsModal";
import { useNotification } from "../context/NotificationContext";
import clubService from "../services/clubService";

const Clubs = () => {
  const { t } = useTranslation("clubs");
  const [showForm, setShowForm] = useState(false);
  const [editingClub, setEditingClub] = useState(null);
  const [viewingClub, setViewingClub] = useState(null);
  const [managingMembers, setManagingMembers] = useState(null);
  const [managingEvents, setManagingEvents] = useState(null);
  const [loading, setLoading] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const { showSuccess } = useNotification();

  const handleCreate = () => {
    setEditingClub(null);
    setShowForm(true);
  };

  const handleEdit = async (club) => {
    try {
      setLoading(true);
      const response = await clubService.getClub(club.id);
      if (response.success) {
        setEditingClub(response.data);
        setShowForm(true);
      } else {
        setEditingClub(club);
        setShowForm(true);
      }
    } catch (error) {
      console.error("Error fetching club details:", error);
      setEditingClub(club);
      setShowForm(true);
    } finally {
      setLoading(false);
    }
  };

  const handleFormSubmit = async (formData) => {
    try {
      setLoading(true);
      let response;

      if (editingClub) {
        response = await clubService.updateClub(editingClub.id, formData);
        if (response.success) {
          showSuccess(t("messages.updateSuccess"));
        }
      } else {
        response = await clubService.createClub(formData);
        if (response.success) {
          showSuccess(t("messages.createSuccess"));
        }
      }

      setShowForm(false);
      setEditingClub(null);
      setRefreshTrigger((prev) => prev + 1);
    } catch (error) {
      console.error("Form submission error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleFormCancel = () => {
    setShowForm(false);
    setEditingClub(null);
  };

  const handleRefresh = () => {
    setRefreshTrigger((prev) => prev + 1);
  };

  return (
    <MainLayout>
      <div className="clubs-page">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div>
            <h1 className="h3 mb-1 fw-bold text-dark">{t("management")}</h1>
            <p className="text-muted mb-0">{t("managementSubtitle")}</p>
          </div>
          <button className="btn btn-primary" onClick={handleCreate}>
            <i className="fas fa-plus me-2"></i>
            {t("addClub")}
          </button>
        </div>

        {showForm && (
          <ClubFormModal
            club={editingClub}
            onSubmit={handleFormSubmit}
            onCancel={handleFormCancel}
            loading={loading}
          />
        )}

        {viewingClub && (
          <ClubDetailModal
            club={viewingClub}
            onClose={() => setViewingClub(null)}
            onEdit={handleEdit}
            onManageMembers={setManagingMembers}
            onManageEvents={setManagingEvents}
          />
        )}

        {managingMembers && (
          <ClubMembersModal
            club={managingMembers}
            onClose={() => setManagingMembers(null)}
            onRefresh={handleRefresh}
          />
        )}

        {managingEvents && (
          <ClubEventsModal
            club={managingEvents}
            onClose={() => setManagingEvents(null)}
            onRefresh={handleRefresh}
          />
        )}

        <ClubList
          onEdit={handleEdit}
          onView={setViewingClub}
          onManageMembers={setManagingMembers}
          onManageEvents={setManagingEvents}
          refreshTrigger={refreshTrigger}
        />
      </div>
    </MainLayout>
  );
};

export default Clubs;
